import { observer } from 'mobx-react-lite'
import { useId } from 'react'
import { useActivation, useScopedViewModel } from '@/hooks/view-model-hooks'
import { PaymentsViewModel } from '@/features/account/PaymentsView/PaymentsViewModel'
import { NewBillingMethodDialogViewModel } from '@/features/billing/profile/NewBillingMethodDialog/NewBillingMethodDialogViewModel'
import { useDocumentTitle } from '@/hooks/document-title'
import { useRootStore } from '@/contexts/root-store-context'
import { SectionHeadline } from '@/components/SectionHeadline'
import { Button } from '@/components/Button'
import { BillingMethodListContainer } from '@/features/billing/profile/BillingMethodList/BillingMethodListContainer'
import { TransactionsTable } from '@/features/billing/transactions/TransactionsTable'
import { BillingContactSection } from '@/features/billing/components/BillingContactSection'
import { NewBillingMethodDialog } from '@/features/billing/profile/NewBillingMethodDialog'
import {
  PaymentsView,
  PaymentsViewSection,
  MethodAddedDialog,
  BillingMethodsEmpty,
} from './index'

export default observer(function PaymentsViewContainer() {
  const paymentMethodsHeadlineId = useId()
  const transactionsHeadlineId = useId()
  const {
    shell: { shellInfo },
  } = useRootStore()

  const vm = useScopedViewModel(({ billing }, needs) => {
    const {
      createBillingMethodListViewModel,
      createCardBillingMethodEditorViewModel,
      createTransactionsTableViewModel,
    } = needs({
      createBillingMethodListViewModel:
        billing.createBillingMethodListViewModel,
      createCardBillingMethodEditorViewModel:
        billing.createCardBillingMethodEditorViewModel,
      createTransactionsTableViewModel:
        billing.createTransactionsTableViewModel,
    })

    const newBillingMethodDialogVm = new NewBillingMethodDialogViewModel(
      createCardBillingMethodEditorViewModel,
    )

    const transactionsTableVm = createTransactionsTableViewModel({
      type: 'User',
    })

    return new PaymentsViewModel(
      createBillingMethodListViewModel(),
      newBillingMethodDialogVm,
      transactionsTableVm,
    )
  })

  useDocumentTitle('Payment details')
  useActivation(vm)

  const loadingProfile = vm.billingMethodsListVm.fetchProfile.pending
  const isShowingEmptyState =
    !loadingProfile && vm.billingMethodsListVm.methods.items.length === 0
  const isShowingTransactions =
    !vm.transactionsTableVm.isEmpty ||
    vm.transactionsTableVm.scrollProvider.fetch.pending

  return (
    <>
      <PaymentsView>
        <PaymentsViewSection>
          <SectionHeadline
            isSkeleton={loadingProfile}
            id={paymentMethodsHeadlineId}
            action={
              !isShowingEmptyState && (
                <Button
                  onPress={() => vm.newBillingMethodDialogVm.newMethod()}
                  label="Add new"
                  variant="text"
                  iconLeft="add"
                  isSkeleton={loadingProfile}
                />
              )
            }
          >
            Payment methods
          </SectionHeadline>

          {isShowingEmptyState ? (
            <BillingMethodsEmpty
              onAction={() => vm.newBillingMethodDialogVm.newMethod()}
            />
          ) : (
            <BillingMethodListContainer
              vm={vm.billingMethodsListVm}
              aria-labelledby={paymentMethodsHeadlineId}
            />
          )}
        </PaymentsViewSection>

        {isShowingTransactions && (
          <PaymentsViewSection>
            <SectionHeadline
              id={transactionsHeadlineId}
              isSkeleton={vm.transactionsTableVm.scrollProvider.fetch.pending}
            >
              Payment history
            </SectionHeadline>
            <TransactionsTable
              variant="compact"
              vm={vm.transactionsTableVm}
              aria-labelledby={transactionsHeadlineId}
            />

            {shellInfo.showTaxfyleInfo && <BillingContactSection />}
          </PaymentsViewSection>
        )}
      </PaymentsView>

      <NewBillingMethodDialog vm={vm.newBillingMethodDialogVm} />
      <MethodAddedDialogContainer vm={vm} workspaceName={shellInfo.name} />
    </>
  )
})

interface MethodAddedDialogContainerProps {
  vm: PaymentsViewModel
  workspaceName: string
}

export const MethodAddedDialogContainer = observer(
  function MethodAddedDialogContainer({
    vm,
    workspaceName,
  }: MethodAddedDialogContainerProps) {
    return (
      <MethodAddedDialog
        isOpen={vm.successModal.showing}
        workspaceName={workspaceName}
        onClose={vm.successModal.close}
      />
    )
  },
)
